import {
  wechatLoginSettingsSchema,
  upsertWechatLoginSettingsSchema,
  adminPaymentOrderDetailSchema,
  adminPaymentOrderListQuerySchema,
  adminPaymentOrderListResultSchema,
  apiErrorSchema,
  apiSuccessSchema,
  appConfigSchema,
  appSchema,
  createAppResultSchema,
  credentialMetadataSchema,
  credentialRevealSchema,
  createPaymentRefundInputSchema,
  payChannelConfigSchema,
  paymentConfigSchema,
  paymentRefundSchema,
  paymentReconciliationBatchSchema,
  paymentReconciliationDifferenceSchema,
  paymentReconciliationQuerySchema,
  paymentStatementBatchSchema,
  paymentStatementJobSchema,
  paymentStatementImportInputSchema,
  paymentStatementWorkspaceSchema,
  paymentWebhookEventDetailSchema,
  paymentWebhookEventListQuerySchema,
  paymentWebhookEventListResultSchema,
  paymentWebhookEventSchema,
  triggerPaymentStatementJobInputSchema,
  updatePaymentDifferenceInputSchema,
  type PayChannel,
} from "@yahshi/contracts";
import { z } from "zod";
import type { AppListFilters, ConfigUpsertInput, M1AppApi } from "./M1AppApi";
import { M1ApiError } from "./M1AppApi";

type HttpM1AppApiOptions = {
  baseUrl: string;
  getAccessToken: () => Promise<string>;
  onUnauthorized?: (error: M1ApiError) => void | Promise<void>;
  fetcher?: typeof fetch;
};

function withoutTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

export function createHttpM1AppApi(options: HttpM1AppApiOptions): M1AppApi {
  const fetcher = options.fetcher ?? fetch;
  const baseUrl = withoutTrailingSlash(options.baseUrl);

  async function notifyUnauthorized(error: M1ApiError): Promise<void> {
    if (error.code === "UNAUTHORIZED" || error.code === "ADMIN_FORBIDDEN") {
      await options.onUnauthorized?.(error);
    }
  }

  async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
    let accessToken: string;
    try {
      accessToken = await options.getAccessToken();
    } catch (reason) {
      if (reason instanceof M1ApiError) await notifyUnauthorized(reason);
      throw reason;
    }
    const response = await fetcher(`${baseUrl}${path}`, {
      ...init,
      headers: {
        authorization: `Bearer ${accessToken}`,
        ...(init?.body ? { "content-type": "application/json" } : {}),
        ...init?.headers,
      },
    });
    let payload: unknown;
    try {
      payload = await response.json() as unknown;
    } catch {
      payload = undefined;
    }
    if (!response.ok) {
      const failure = apiErrorSchema.safeParse(payload);
      const error = failure.success
        ? new M1ApiError(failure.data.error.code, failure.data.error.message, failure.data.error.fields)
        : new M1ApiError(response.status === 401 ? "UNAUTHORIZED" : response.status === 403 ? "ADMIN_FORBIDDEN" : "HTTP_ERROR", `请求失败 (${response.status})`);
      await notifyUnauthorized(error);
      throw error;
    }
    const envelope = apiSuccessSchema(schema).safeParse(payload);
    if (!envelope.success) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
    return envelope.data.data;
  }

  return {
    async listApps(filters: AppListFilters) {
      const query = new URLSearchParams({
        keyword: filters.keyword,
        status: filters.status,
        page: String(filters.page ?? 1),
        pageSize: String(filters.pageSize ?? 20),
      });
      return request(`/admin/apps?${query}`, z.object({
        items: z.array(appSchema),
        total: z.number().int().nonnegative(),
        activeTotal: z.number().int().nonnegative(),
      }));
    },
    getApp(appId) {
      return request(`/admin/apps/${encodeURIComponent(appId)}`, appSchema);
    },
    createApp(input) {
      return request("/admin/apps", createAppResultSchema, { method: "POST", body: JSON.stringify(input) });
    },
    updateApp(appId, input) {
      return request(`/admin/apps/${encodeURIComponent(appId)}`, appSchema, { method: "PATCH", body: JSON.stringify(input) });
    },
    async deleteApp(appId) {
      await request(`/admin/apps/${encodeURIComponent(appId)}`, z.object({ deleted: z.literal(true) }), { method: "DELETE" });
    },
    getCredential(appId) {
      return request(`/admin/apps/${encodeURIComponent(appId)}/credential`, credentialMetadataSchema);
    },
    resetCredential(appId) {
      return request(`/admin/apps/${encodeURIComponent(appId)}/credential/reset`, credentialRevealSchema, {
        method: "POST",
        body: JSON.stringify({ confirmation: "RESET" }),
      });
    },
    listConfigs(appId) {
      return request(`/admin/apps/${encodeURIComponent(appId)}/configs`, z.array(appConfigSchema));
    },
    getWechatLoginSettings(appId) {
      return request(`/admin/apps/${encodeURIComponent(appId)}/wechat-login`, wechatLoginSettingsSchema);
    },
    upsertWechatLoginSettings(appId, input) {
      return request(`/admin/apps/${encodeURIComponent(appId)}/wechat-login`, wechatLoginSettingsSchema, { method: "PUT", body: JSON.stringify(upsertWechatLoginSettingsSchema.parse(input)) });
    },
    upsertConfig(appId, input: ConfigUpsertInput) {
      const { key, ...body } = input;
      return request(`/admin/apps/${encodeURIComponent(appId)}/configs/${encodeURIComponent(key)}`, appConfigSchema, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    async deleteConfig(appId, key) {
      await request(`/admin/apps/${encodeURIComponent(appId)}/configs/${encodeURIComponent(key)}`, z.object({ deleted: z.literal(true) }), { method: "DELETE" });
    },
    listPayChannels(appId) {
      return request(`/admin/apps/${encodeURIComponent(appId)}/pay-channels`, z.array(payChannelConfigSchema));
    },
    upsertPayChannel(appId, input) {
      return request(
        `/admin/apps/${encodeURIComponent(appId)}/pay-channels/${encodeURIComponent(input.channel)}`,
        payChannelConfigSchema,
        { method: "PUT", body: JSON.stringify(input) },
      );
    },
    async deletePayChannel(appId, channel: PayChannel) {
      await request(
        `/admin/apps/${encodeURIComponent(appId)}/pay-channels/${encodeURIComponent(channel)}`,
        z.object({ deleted: z.literal(true) }),
        { method: "DELETE" },
      );
    },
    listPaymentOrders(input) {
      const query = adminPaymentOrderListQuerySchema.parse(input);
      const params = new URLSearchParams({
        keyword: query.keyword,
        status: query.status,
        appId: query.appId,
        channel: query.channel,
        page: String(query.page),
        pageSize: String(query.pageSize),
      });
      return request(`/admin/payments/orders?${params}`, adminPaymentOrderListResultSchema);
    },
    getPaymentOrder(orderId) {
      return request(`/admin/payments/orders/${encodeURIComponent(orderId)}`, adminPaymentOrderDetailSchema);
    },
    async syncPaymentOrder(orderId) {
      await request(`/admin/payments/orders/${encodeURIComponent(orderId)}/sync`, z.object({ synced: z.literal(true) }), { method: "POST" });
    },
    async syncPaymentRefund(orderId, refundId) {
      await request(`/admin/payments/orders/${encodeURIComponent(orderId)}/refunds/${encodeURIComponent(refundId)}/sync`, paymentRefundSchema, { method: "POST" });
    },
    getPaymentReconciliation(input) {
      const query = paymentReconciliationQuerySchema.parse(input);
      const params = new URLSearchParams({ dateFrom: query.dateFrom, dateTo: query.dateTo, channel: query.channel });
      return request(`/admin/payments/reconciliation?${params}`, paymentReconciliationBatchSchema);
    },
    getPaymentStatementWorkspace(input) {
      const query = paymentReconciliationQuerySchema.parse(input);
      const params = new URLSearchParams({ dateFrom: query.dateFrom, dateTo: query.dateTo, channel: query.channel });
      return request(`/admin/payments/reconciliation/statements?${params}`, paymentStatementWorkspaceSchema);
    },
    importPaymentStatement(input) {
      const parsed = paymentStatementImportInputSchema.parse(input);
      return request("/admin/payments/reconciliation/statements", paymentStatementBatchSchema, { method: "POST", body: JSON.stringify(parsed) });
    },
    rerunPaymentStatement(batchId) {
      return request(`/admin/payments/reconciliation/statements/${encodeURIComponent(batchId)}/rerun`, paymentStatementBatchSchema, { method: "POST" });
    },
    triggerPaymentStatementJob(input) {
      return request("/admin/payments/reconciliation/jobs", paymentStatementJobSchema, { method: "POST", body: JSON.stringify(triggerPaymentStatementJobInputSchema.parse(input)) });
    },
    updatePaymentDifference(differenceId, input) {
      return request(`/admin/payments/reconciliation/differences/${encodeURIComponent(differenceId)}`, paymentReconciliationDifferenceSchema, { method: "PUT", body: JSON.stringify(updatePaymentDifferenceInputSchema.parse(input)) });
    },
    listPaymentWebhookEvents(input) {
      const query = paymentWebhookEventListQuerySchema.parse(input);
      const params = new URLSearchParams({ keyword: query.keyword, channel: query.channel, eventType: query.eventType, status: query.status, dateFrom: query.dateFrom, dateTo: query.dateTo, page: String(query.page), pageSize: String(query.pageSize) });
      return request(`/admin/payments/notifications?${params}`, paymentWebhookEventListResultSchema);
    },
    getPaymentWebhookEvent(eventId) {
      return request(`/admin/payments/notifications/${encodeURIComponent(eventId)}`, paymentWebhookEventDetailSchema);
    },
    retryPaymentWebhookEvent(eventId) {
      return request(`/admin/payments/notifications/${encodeURIComponent(eventId)}/retry`, paymentWebhookEventSchema, { method: "POST" });
    },
    createPaymentRefund(orderId, input) {
      const parsed = createPaymentRefundInputSchema.parse(input);
      return request(`/admin/payments/orders/${encodeURIComponent(orderId)}/refund`, paymentRefundSchema, { method: "POST", body: JSON.stringify(parsed) });
    },
    listPaymentConfigs() {
      return request("/admin/payments/configurations", z.array(paymentConfigSchema));
    },
    upsertPaymentConfig(id, input) {
      return request(id ? `/admin/payments/configurations/${encodeURIComponent(id)}` : "/admin/payments/configurations", paymentConfigSchema, {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(input),
      });
    },
    async deletePaymentConfig(id) {
      await request(`/admin/payments/configurations/${encodeURIComponent(id)}`, z.object({ deleted: z.literal(true) }), { method: "DELETE" });
    },
    async linkPaymentConfig(appId, configId) {
      await request(`/admin/apps/${encodeURIComponent(appId)}/payment-configurations/${encodeURIComponent(configId)}`, z.object({ linked: z.literal(true) }), { method: "PUT" });
    },
    async unlinkPaymentConfig(appId, configId) {
      await request(`/admin/apps/${encodeURIComponent(appId)}/payment-configurations/${encodeURIComponent(configId)}`, z.object({ unlinked: z.literal(true) }), { method: "DELETE" });
    },
  };
}
